<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { ChevronDown, KeyRound, LogOut, UserRound } from "@lucide/vue";
import { useAuth } from "./composables/useAuth";
import { useRouter } from "vue-router";

const { user, isLoggedIn, isAdmin, isCoach, isAssistant, isSupervisionCoordinator, roleLabel, fetchMe, logout } = useAuth();
const router = useRouter();

const displayLabel = computed(() => user.value?.displayName || user.value?.phone || user.value?.username || "");
const isAccountMenuOpen = ref(false);
const accountMenuRef = ref<HTMLElement | null>(null);

if (isLoggedIn.value) {
  void fetchMe();
}

function handleLogout(): void {
  isAccountMenuOpen.value = false;
  logout();
  router.replace("/login");
}

function closeAccountMenu(): void {
  isAccountMenuOpen.value = false;
}

function handleDocumentClick(event: MouseEvent): void {
  if (accountMenuRef.value && !accountMenuRef.value.contains(event.target as Node)) closeAccountMenu();
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape") closeAccountMenu();
}

onMounted(() => {
  document.addEventListener("click", handleDocumentClick);
  document.addEventListener("keydown", handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick);
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<template>
  <div class="app-shell">
    <header class="app-header" v-if="isLoggedIn">
      <div class="header-left">
        <h1 class="app-title">七天营运营工作台</h1>
        <nav class="tab-nav">
          <RouterLink v-if="isAdmin" to="/current-coaches" class="tab-link">当期工作区</RouterLink>
          <RouterLink v-if="isCoach" to="/coach-workspace" class="tab-link">教练填写</RouterLink>
          <RouterLink v-if="isAssistant" to="/assistant-workspace" class="tab-link">小棠X填写</RouterLink>
          <RouterLink v-if="isSupervisionCoordinator" to="/supervision-workspace" class="tab-link">督导协调</RouterLink>
          <RouterLink v-if="isAdmin" :to="{ path: '/coach-workspace', query: { preview: '1' } }" class="tab-link">教练端预览</RouterLink>
          <RouterLink v-if="isAdmin" :to="{ path: '/assistant-workspace', query: { preview: '1' } }" class="tab-link">小棠X端预览</RouterLink>
          <RouterLink v-if="isAdmin" :to="{ path: '/supervision-workspace', query: { preview: '1' } }" class="tab-link">督导协调预览</RouterLink>
          <RouterLink v-if="isAdmin" to="/current-term" class="tab-link">学员信息库</RouterLink>
          <RouterLink v-if="isAdmin" to="/deferred" class="tab-link">延期学员库</RouterLink>
          <RouterLink v-if="isAdmin" to="/coach-library" class="tab-link">教练库</RouterLink>
          <RouterLink v-if="isAdmin" to="/serving-coaches" class="tab-link">当期服务教练</RouterLink>
          <RouterLink v-if="isAdmin" to="/accounts" class="tab-link">账号管理</RouterLink>
        </nav>
      </div>
      <div class="header-right">
        <div ref="accountMenuRef" class="account-menu" v-if="user">
          <button
            type="button"
            class="account-trigger"
            :class="{ open: isAccountMenuOpen }"
            :aria-expanded="isAccountMenuOpen"
            aria-haspopup="menu"
            @click="isAccountMenuOpen = !isAccountMenuOpen"
          >
            <span class="account-avatar"><UserRound :size="16" /></span>
            <span class="account-summary"><strong>{{ displayLabel }}</strong><small>{{ roleLabel }}</small></span>
            <ChevronDown class="account-chevron" :class="{ open: isAccountMenuOpen }" :size="15" />
          </button>
          <Transition name="account-popover">
            <div v-if="isAccountMenuOpen" class="account-popover" role="menu" aria-label="账户菜单">
              <div class="account-profile">
                <span class="account-profile-avatar"><UserRound :size="18" /></span>
                <div><strong>{{ displayLabel }}</strong><small>{{ user.phone || user.username }}</small><em>{{ roleLabel }}</em></div>
              </div>
              <div class="account-menu-divider"></div>
              <RouterLink to="/password" class="account-menu-item" role="menuitem" @click="closeAccountMenu"><KeyRound :size="16" />修改密码</RouterLink>
              <div class="account-menu-divider"></div>
              <button type="button" class="account-menu-item logout-menu-item" role="menuitem" @click="handleLogout"><LogOut :size="16" />退出登录</button>
            </div>
          </Transition>
        </div>
      </div>
    </header>
    <main class="app-main">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100vh;
  background: #f5f7fb;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 28px;
  min-height: 60px;
  background: linear-gradient(135deg, #cf7a42 0%, #db9458 50%, #e2a676 100%);
  box-shadow: 0 2px 12px rgba(74, 124, 89, 0.2);
}

.header-left {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 28px;
}

.app-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0,0,0,0.08);
}

.tab-nav {
  display: flex;
  min-width: 0;
  flex: 1;
  gap: 4px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.tab-nav::-webkit-scrollbar { display: none; }

.tab-link {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  height: 34px;
  padding: 0 16px;
  border-radius: 8px;
  color: rgba(255,255,255,0.8);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.tab-link:hover {
  color: #fff;
  background: rgba(255,255,255,0.15);
}

.tab-link.router-link-active {
  background: rgba(255,255,255,0.25);
  color: #fff;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.header-right {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.account-menu { position:relative; }.account-trigger { display:flex; align-items:center; gap:8px; min-height:40px; padding:4px 9px 4px 5px; border:1px solid transparent; border-radius:10px; background:transparent; color:#fff; cursor:pointer; text-align:left; transition:background .2s ease,border-color .2s ease; }.account-trigger:hover,.account-trigger.open { border-color:rgba(255,255,255,.2); background:rgba(255,255,255,.14); }.account-avatar,.account-profile-avatar { display:grid; place-items:center; border-radius:50%; }.account-avatar { width:29px; height:29px; background:#f5ede4; color:#cf7a42; }.account-summary { display:grid; min-width:0; gap:1px; }.account-summary strong { overflow:hidden; max-width:132px; color:#fff; font-size:13px; font-weight:700; text-overflow:ellipsis; white-space:nowrap; }.account-summary small { color:rgba(255,255,255,.72); font-size:11px; }.account-chevron { margin-left:1px; color:rgba(255,255,255,.82); transition:transform .2s ease; }.account-chevron.open { transform:rotate(180deg); }.account-popover { position:absolute; z-index:500; top:calc(100% + 9px); right:0; width:236px; overflow:hidden; border:1px solid #e8ddd0; border-radius:12px; background:#fffdfc; box-shadow:0 14px 34px rgba(20,49,38,.18); color:#3a2510; }.account-profile { display:flex; align-items:center; gap:10px; padding:14px; background:#faf7f2; }.account-profile-avatar { flex:0 0 auto; width:34px; height:34px; background:#f2e8dc; color:#cf7a42; }.account-profile div { display:grid; min-width:0; gap:2px; }.account-profile strong { overflow:hidden; font-size:13px; text-overflow:ellipsis; white-space:nowrap; }.account-profile small { overflow:hidden; color:#9c8c7b; font-size:11px; text-overflow:ellipsis; white-space:nowrap; }.account-profile em { width:max-content; margin-top:3px; padding:2px 7px; border-radius:9px; background:#f2e8dc; color:#cf7a42; font-size:10px; font-style:normal; font-weight:700; }.account-menu-divider { height:1px; margin:5px 0; background:#f0e8dc; }.account-menu-item { display:flex; box-sizing:border-box; width:100%; align-items:center; gap:9px; padding:10px 14px; border:0; background:transparent; color:#3a2510; font:600 13px "Noto Sans SC","PingFang SC","Microsoft YaHei",sans-serif; text-align:left; text-decoration:none; cursor:pointer; transition:background .16s ease; }.account-menu-item:hover { background:#faf7f2; }.logout-menu-item { color:#cf7a42; }.logout-menu-item:hover { background:#fff4f2; }.account-popover-enter-active,.account-popover-leave-active { transition:opacity .14s ease,transform .14s ease; }.account-popover-enter-from,.account-popover-leave-to { opacity:0; transform:translateY(-5px) scale(.98); }

.app-main {
  padding: 24px;
}

@media (max-width: 1100px) {
  .app-header { padding: 0 18px; gap: 12px; }
  .header-left { gap: 18px; }
  .tab-link { padding: 0 13px; }
  .account-summary { display:none; }
}

@media (max-width: 640px) {
  .app-header {
    position: relative;
    display: block;
    min-height: 0;
    padding: 14px 0 0;
  }
  .header-left { display: block; }
  .app-title { padding: 0 126px 14px 16px; font-size: 16px; }
  .header-right { position: absolute; top: 8px; right: 12px; }
  .account-trigger { min-height:34px; padding:3px 6px 3px 3px; }.account-avatar { width:27px; height:27px; }.account-popover { top:calc(100% + 7px); width:224px; }
  .tab-nav { gap: 6px; padding: 0 16px 12px; }
  .tab-link { height: 32px; padding: 0 12px; border-radius: 16px; font-size: 12px; }
  .app-main { padding: 14px; }
}
</style>
